/**
 * TOSS Web Application — the portal's one answer to "how wide are we".
 *
 * The client zone called `useResponsive()` in 27 places, and each call installed its own resize
 * listener with its own 100 ms debounce and its own state. This is one listener, read by everyone.
 *
 * **The numbers are Tailwind v4's real defaults, and that is the whole point.** `tailwind.config.js`
 * declares a different scale (`lg` = 768, `xl` = 1024) but is inert: `index.css` imports Tailwind
 * without a `@config` directive, so what actually ships is `sm` 640 / `md` 768 / `lg` 1024 /
 * `xl` 1280. Half the client code was written against the declared scale and half against the real
 * one. Naming the real numbers here — and matching them to the class prefixes — is what stops the
 * next person from writing `lg:` meaning "tablet".
 *
 *   phone   ≤ 767   — bottom bar, one column
 *   tablet  768–1023 — `md:`, sidebar collapsed to icons
 *   desktop ≥ 1024  — `lg:`, sidebar with labels
 *   wide    ≥ 1280  — `xl:`
 *
 * The internal app keeps using `hooks/useResponsive` — it is shared, and this file must not reach
 * into it beyond mirroring the one number that decides the portal's phone break.
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react';

/** The portal's width boundaries. Mirrors what Tailwind actually compiles, see the file docblock. */
export const CLIENT_BREAKPOINTS = {
  /** Above this width the phone chrome gives way to the sidebar. */
  phoneMax: 767,
  /** `md:` — sidebar appears, collapsed to icons. */
  tablet: 768,
  /** `lg:` — sidebar shows labels. */
  desktop: 1024,
  /** `xl:` — room for a second column beside the content. */
  wide: 1280,
} as const;

export interface ClientViewport {
  /** ≤ 767: bottom bar, no sidebar. */
  isPhone: boolean;
  /** 768–1023: sidebar, collapsed to icons and not togglable. */
  isTablet: boolean;
  /** ≥ 1024: sidebar with labels. */
  isDesktop: boolean;
  /** ≥ 1280. */
  isWide: boolean;
  /**
   * Same meaning and same number as `useResponsive().showMobileNav`, kept under its old name so
   * that moving a component onto this hook is a rename and never a behaviour change.
   */
  showMobileNav: boolean;
}

function measure(width: number): ClientViewport {
  const isPhone = width <= CLIENT_BREAKPOINTS.phoneMax;

  return {
    isPhone,
    isTablet: !isPhone && width < CLIENT_BREAKPOINTS.desktop,
    isDesktop: width >= CLIENT_BREAKPOINTS.desktop,
    isWide: width >= CLIENT_BREAKPOINTS.wide,
    showMobileNav: isPhone,
  };
}

export function readClientViewport(): ClientViewport {
  // SSR-safe and jsdom-safe: tests that never touch `innerWidth` get the desktop answer, which is
  // what the existing suite already assumes.
  const width = typeof window === 'undefined' ? CLIENT_BREAKPOINTS.wide : window.innerWidth;
  return measure(width);
}

/** Shared with the provider component next door; not part of the public surface. */
export const ClientViewportContext = createContext<ClientViewport | null>(null);

/**
 * The portal's viewport. Works WITHOUT the provider too — a component rendered on its own (which
 * is what 31 existing tests do) measures for itself rather than failing. The provider is an
 * optimisation, not a requirement.
 */
export function useClientViewport(): ClientViewport {
  const shared = useContext(ClientViewportContext);
  const [standalone, setStandalone] = useState<ClientViewport>(readClientViewport);

  useEffect(() => {
    if (shared) return undefined;

    const onResize = (): void => setStandalone(readClientViewport());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [shared]);

  const fallback = useMemo(() => standalone, [standalone]);
  return shared ?? fallback;
}
